'use client'

import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { supabase } from '@/integrations/supabase/client'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useTranslation } from 'react-i18next'
import { CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react'
import { useFacilityBranding } from '@/contexts/SubdomainBrandingContext'
import BrandLogo from '@/components/common/BrandLogo'

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const facilityBranding = useFacilityBranding()
  const isBrandedSubdomain = facilityBranding?.is_branded ?? false
  const orgAccent = facilityBranding?.secondary_color || '#10b981'

  useEffect(() => {
    // Supabase sends tokens as hash fragments (#access_token=...&refresh_token=...)
    // The Supabase client auto-detects these and sets the session.
    // We also check query params as a fallback.
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''))
    const queryParams = searchParams
    const accessToken = hashParams.get('access_token') || queryParams.get('access_token')
    const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token')
    const type = hashParams.get('type') || queryParams.get('type')
    
    // If no tokens and no recovery type, check if we already have an active session
    // (Supabase client may have already consumed the hash)
    if (!accessToken && !refreshToken && type !== 'recovery') {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) {
          setError(t('auth.reset.invalidLink', 'Invalid or expired reset link. Please request a new password reset.'))
        }
      })
    }
  }, [searchParams, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    // Validation
    if (password.length < 6) {
      setError(t('auth.reset.tooShort', 'Password must be at least 6 characters long.'))
      setIsSubmitting(false)
      return
    }

    if (password !== confirmPassword) {
      setError(t('auth.reset.mismatch', 'Passwords do not match.'))
      setIsSubmitting(false)
      return
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) {
        setError(error.message)
      } else {
        setSuccess(true)
        setTimeout(() => {
          navigate('/auth/login')
        }, 2000)
      }
    } catch {
      setError(t('auth.reset.unexpected', 'An unexpected error occurred. Please try again.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20 flex items-center justify-center py-12 px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-md w-full"
        >
          <motion.div
            variants={itemVariants}
            className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-emerald-200/50 p-8 text-center"
          >
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-800 mb-4">{t('auth.reset.successTitle', 'Password Updated!')}</h2>
            <p className="text-slate-600 mb-6">
              {t('auth.reset.successDesc', 'Your password has been successfully updated. You can now sign in with your new password.')}
            </p>
            <Button
              onClick={() => navigate('/auth/login')}
              className="w-full h-12 text-lg font-semibold rounded-xl"
            >
              {t('auth.login.signIn', 'Sign In')}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-20">
        <motion.div 
          className="absolute top-20 left-20 w-96 h-96 bg-emerald-400 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div 
          className="absolute bottom-20 right-20 w-80 h-80 bg-indigo-400 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-md w-full">
          <motion.div variants={itemVariants} className="text-center mb-8">
            {/* Org logo or default lock emoji */}
            {isBrandedSubdomain ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                {facilityBranding?.logo_url ? (
                  <BrandLogo src={facilityBranding.logo_url} alt={facilityBranding.display_name} style={{ height: 64, objectFit: 'contain' }} />
                ) : (
                  <div style={{
                    width: 64, height: 64, borderRadius: 16,
                    background: `${orgAccent}18`, border: `1px solid ${orgAccent}44`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
                  }}>🏢</div>
                )}
                <p style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{facilityBranding?.display_name}</p>
              </div>
            ) : (
              <motion.div 
                className="text-6xl mb-6 filter drop-shadow-lg"
                animate={{ 
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.05, 1]
                }}
                transition={{ 
                  duration: 4, 
                  repeat: Infinity,
                  repeatDelay: 2
                }}
              >
                🔐
              </motion.div>
            )}
            <h2 className="text-4xl font-bold text-slate-800 mb-2">{t('auth.resetPassword.title', 'Reset Password')}</h2>
            <p className="text-lg text-slate-600">
              {t('auth.resetPassword.subtitle', 'Enter your new password below')}
            </p>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-emerald-200/50 p-8"
          >
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center"
              >
                <XCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-lg font-semibold text-slate-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your new password"
                    className="text-lg h-14 px-4 pr-12 rounded-xl border-2 border-slate-200 focus:border-emerald-500 transition-colors duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-lg font-semibold text-slate-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm your new password"
                    className="text-lg h-14 px-4 pr-12 rounded-xl border-2 border-slate-200 focus:border-emerald-500 transition-colors duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-14 text-lg font-semibold rounded-xl"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner size="sm" className="mr-3" />
                    Updating Password...
                  </div>
                ) : (
                  'Update Password'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                Remember your password?{' '}
                <button
                  onClick={() => navigate('/auth/login')}
                  className="font-semibold text-emerald-600 hover:text-emerald-700 transition-colors duration-200"
                >
                  Sign In
                </button>
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}

